import React, { useState } from 'react';
import { productos, categorias } from '../data/mockData';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Shirt, Package, ChevronLeft, ChevronRight } from 'lucide-react';

const ProductImageCarousel = ({ imagenes, nombre }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  // Mínimo de distancia para considerar swipe
  const minSwipeDistance = 50;
  
  // Si no hay imágenes o es un array vacío, mostrar placeholder
  if (!imagenes || imagenes.length === 0) {
    return (
      <div className="relative bg-soft-gray aspect-square flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-soft-taupe to-primary-pink/20"></div>
        <Shirt size={80} className="text-primary-pink/40" />
      </div>
    );
  }

  const nextImage = (e) => {
    if (e) e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % imagenes.length);
  };

  const prevImage = (e) => {
    if (e) e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + imagenes.length) % imagenes.length);
  };

  // Handlers para swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      nextImage();
    } else if (isRightSwipe) {
      prevImage();
    }
  };

  return (
    <div 
      className="relative bg-soft-gray aspect-square overflow-hidden group"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <img
        src={imagenes[currentImageIndex]}
        alt={`${nombre} - ${currentImageIndex + 1}`}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.parentElement.innerHTML = `
            <div class="absolute inset-0 bg-gradient-to-br from-soft-taupe to-primary-pink/20 flex items-center justify-center">
              <svg class="text-primary-pink/40" width="80" height="80" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5M2 12l10 5 10-5"></path>
              </svg>
            </div>
          `;
        }}
      />
      
      {/* Flechas de navegación - solo si hay más de 1 imagen */}
      {imagenes.length > 1 && (
        <>
          <button
            onClick={prevImage}
            className="carousel-nav-btn absolute left-2 top-1/2 bg-charcoal/70 text-cream-white p-2 rounded-full z-10 opacity-70 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 active:bg-charcoal select-none"
            aria-label="Imagen anterior"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={nextImage}
            className="carousel-nav-btn absolute right-2 top-1/2 bg-charcoal/70 text-cream-white p-2 rounded-full z-10 opacity-70 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 active:bg-charcoal select-none"
            aria-label="Siguiente imagen"
          >
            <ChevronRight size={20} />
          </button>
          
          {/* Indicadores de posición */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {imagenes.map((_, idx) => (
              <div
                key={idx}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  idx === currentImageIndex 
                    ? 'bg-primary-pink w-6' 
                    : 'bg-cream-white/50'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Catalog = () => {
  const [selectedCategory, setSelectedCategory] = useState('todos');
  const [selectedProduct, setSelectedProduct] = useState(null);

  const filteredProducts = selectedCategory === 'todos'
    ? productos
    : productos.filter(p => p.categorias && p.categorias.includes(selectedCategory));

  const handleProductClick = (product) => {
    setSelectedProduct(product);
  };

  const closeModal = () => {
    setSelectedProduct(null);
  };

  return (
    <section id="catalogo" className="py-24 bg-cream-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-primary-pink text-sm tracking-widest uppercase mb-4 font-medium">
            Nuestros Productos
          </p>
          <h2 className="text-4xl md:text-5xl font-serif text-charcoal mb-6">
            Catálogo D-Lyrio
          </h2>
          <div className="w-24 h-1 bg-primary-pink mx-auto mb-6"></div>
          <p className="text-warm-gray text-lg max-w-2xl mx-auto">
            Todos los precios incluyen diseño personalizado. Precios especiales por mayor desde 5 prendas.
            <br></br>
            <br></br>
            <strong>Consulte por otros colores.</strong>
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categorias.map((cat) => (
            <Button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              variant={selectedCategory === cat.id ? 'default' : 'outline'}
              className={`px-6 py-2 rounded-full font-medium tracking-wide transition-all duration-300 ${
                selectedCategory === cat.id
                  ? 'bg-primary-pink text-cream-white hover:bg-deep-navy'
                  : 'border-primary-pink text-primary-pink hover:bg-primary-pink hover:text-cream-white'
              }`}
            >
              {cat.nombre}
            </Button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {filteredProducts.map((producto) => (
            <Card
              key={producto.id}
              className="group bg-white border-none shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden cursor-pointer"
              onClick={() => handleProductClick(producto)}
            >
              {/* Product Image Carousel */}
              <ProductImageCarousel imagenes={producto.imagenes} nombre={producto.nombre} />
              
              {/* Price Badge */}
              <div className="absolute top-4 right-4 z-20">
                <Badge className="bg-primary-pink text-cream-white text-lg font-semibold px-4 py-2 shadow-lg">
                  ${producto.precio}
                </Badge>
              </div>

              {/* Product Info */}
              <div className="p-6">
                <h3 className="text-xl font-serif text-charcoal mb-2 group-hover:text-primary-pink transition-colors duration-300">
                  {producto.nombre}
                </h3>
                <p className="text-warm-gray text-sm mb-4">
                  {producto.descripcion}
                </p>

                {/* Material */}
                <div className="flex items-center gap-2 mb-3">
                  <Package size={16} className="text-primary-pink" />
                  <span className="text-xs text-warm-gray">{producto.material}</span>
                </div>

                {/* Color Dots */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs text-warm-gray">Colores:</span>
                  <div className="flex gap-1">
                    {producto.colores.slice(0, 4).map((color, idx) => (
                      <div
                        key={idx}
                        className="w-4 h-4 rounded-full border border-warm-gray/30"
                        style={{
                          backgroundColor: 
                            color === 'Negro' ? '#2C2C2C' :
                            color === 'Blanco' ? '#FFFFFF' :
                            color === 'Beige' ? '#D4C4B0' :
                            color === 'Verde' ? '#6B8E6B' :
                            color === 'Verde Irlandes' ? '#4B8C4B' :
                            color === 'Azul Piedra' ? '#277bbb' :
                            color === 'Azul Marino' ? '#003366' :
                            color === 'Gris' ? '#7d7975' :
                            color === 'Gris Melange' ? '#b5b4b0' :
                            color === 'Uva' ? '#800080' :
                            '#c4c0a0'
                        }}
                      ></div>
                    ))}
                    {producto.colores.length > 4 && (
                      <span className="text-xs text-warm-gray">+{producto.colores.length - 4}</span>
                    )}
                  </div>
                </div>

                {/* Sizes */}
                <div className="flex flex-wrap gap-2">
                  {producto.talles.map((talle, idx) => (
                    <span
                      key={idx}
                      className="text-xs px-2 py-1 bg-soft-gray text-warm-gray rounded"
                    >
                      {talle}
                    </span>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Modal */}
        {selectedProduct && (
          <div
            className="fixed inset-0 bg-charcoal/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <Card
              className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-8">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-3xl font-serif text-charcoal mb-2">
                      {selectedProduct.nombre}
                    </h3>
                    <p className="text-2xl font-semibold text-primary-pink">
                      ${selectedProduct.precio}
                    </p>
                  </div>
                  <button
                    onClick={closeModal}
                    className="text-warm-gray hover:text-charcoal transition-colors"
                  >
                    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 6l12 12M6 18L18 6" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-charcoal mb-2">Descripción</h4>
                    <p className="text-warm-gray">{selectedProduct.descripcion}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-charcoal mb-2">Material</h4>
                    <p className="text-warm-gray">{selectedProduct.material}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-charcoal mb-2">Talles Disponibles</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProduct.talles.map((talle, idx) => (
                        <span
                          key={idx}
                          className="px-4 py-2 bg-soft-gray text-charcoal rounded font-medium"
                        >
                          {talle}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-charcoal mb-2">Colores Disponibles</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProduct.colores.map((color, idx) => (
                        <span
                          key={idx}
                          className="px-4 py-2 bg-soft-gray text-charcoal rounded text-sm"
                        >
                          {color}
                        </span>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={() => {
                      closeModal();
                      const element = document.getElementById('cotizacion');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="w-full bg-primary-pink hover:bg-deep-navy text-cream-white py-3 rounded font-medium tracking-wide transition-colors duration-300"
                  >
                    COTIZAR ESTE PRODUCTO
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Info Banner */}
        <div className="mt-16">
          <Card className="bg-dusty-rose/30 border-none p-8 text-center">
            <p className="text-charcoal text-lg mb-2">
              💡 <strong>Nota:</strong> Todos los precios incluyen diseño personalizado
            </p>
            <p className="text-warm-gray">
              Precio especial por mayor a partir de 5 prendas • Garantía de 1 mes • Envíos a todo Uruguay • Consulte por otros colores
            </p>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Catalog;
